'use client'

import { useMemo, useState } from 'react'

import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { LoadingSpinner } from '~/components/component-library/LoadingSpinner'
import { Tabs } from '~/components/component-library/Tabs'
import {
  CHART_HEIGHT_PX,
  PriceChart,
} from '~/components/smart-accounts/TokenPriceChartDialog/PriceChart'
import { PriceChartHeader } from '~/components/smart-accounts/TokenPriceChartDialog/PriceChartHeader'
import { SupportedChainId } from '~/constants/chains'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import {
  PriceChartPeriod,
  PricePoint,
  useTokenPriceChart,
} from '~/hooks/useTokenPriceChart'
import { IAddress } from '~/types'

const PERIOD_OPTIONS: { value: PriceChartPeriod; display: string }[] = [
  { value: '24h', display: '24H' },
  { value: '7d', display: '7D' },
  { value: '30d', display: '30D' },
  { value: '1y', display: '1Y' },
]

export const TokenPriceChartDialog = ({
  token,
  chainId,
}: {
  token: IAddress
  chainId: SupportedChainId
}) => {
  const [period, setPeriod] = useState<PriceChartPeriod>('7d')
  const { symbol } = useTokenMetadata({ chainId, address: token })
  const title = symbol ? `${symbol} price chart` : 'Token price chart'

  return (
    <SplitsDialogContent id="tokenPriceChart" variant="info" title={title}>
      <div className="flex flex-col gap-4">
        <Tabs
          value={period}
          onValueChange={(value) => setPeriod(value as PriceChartPeriod)}
          options={PERIOD_OPTIONS}
        />
        <PriceChartBody chainId={chainId} token={token} period={period} />
      </div>
    </SplitsDialogContent>
  )
}

const FixedHeight = ({ children }: { children: React.ReactNode }) => (
  <div
    className="flex items-center justify-center text-center text-sm text-secondary"
    style={{ height: CHART_HEIGHT_PX }}
  >
    {children}
  </div>
)

const PriceChartBody = ({
  chainId,
  token,
  period,
}: {
  chainId: SupportedChainId
  token: IAddress
  period: PriceChartPeriod
}) => {
  const { data, isLoading, isError } = useTokenPriceChart({
    chainId,
    address: token,
    period,
  })
  const { price: spot } = useTokenMetadata({
    chainId,
    address: token,
    includePrice: true,
  })
  const openedAt = useMemo(
    // eslint-disable-next-line react-hooks/purity -- captured once at mount; spot point's "now"
    () => Date.now(),
    [],
  )

  if (isLoading) {
    return (
      <FixedHeight>
        <LoadingSpinner size="xl" />
      </FixedHeight>
    )
  }

  if (isError) {
    return <FixedHeight>Couldn&apos;t load price chart. Try again.</FixedHeight>
  }

  const prices = data?.prices ?? []
  if (prices.length === 0) {
    return (
      <FixedHeight>
        {data?.coinId == null
          ? 'Price chart not available for this token'
          : 'No price data in this range'}
      </FixedHeight>
    )
  }

  const pricesWithSpot: PricePoint[] =
    spot !== undefined && spot > 0
      ? [...prices, { t: openedAt, p: spot }]
      : prices

  return (
    <>
      <PriceChartHeader prices={pricesWithSpot} />
      <PriceChart prices={pricesWithSpot} period={period} />
    </>
  )
}
